import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Snackbar,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { getImageUrl } from '../../utils/imageUtils';

// ---- Interfaces ----

interface Product {
  id: number;
  sku: string;
  name: string;
  category_name: string;
  category_id: number;
  unit_size: string;
  qty_in_box: number;
  unit_type: string;
  image_url: string;
  cost_price: number;
  stock_quantity: number;
  stock_single: number;
}

interface Category {
  id: number | string;
  name: string;
}

interface Supplier {
  id: number;
  name: string;
}

interface SupplierOrderItem {
  id?: number;
  product_id: number | null;
  product_name: string;
  product_sku: string;
  unit_size: string;
  unit_type: string;
  qty_in_box: number;
  cost_price: number;
  box_quantity: number;
  single_quantity: number;
  each_price: string;
  offer_price: string;
  notes: string;
  is_new_product: boolean;
  new_product_data?: any;
  // For display only
  image_url?: string;
  stock_quantity?: number;
  stock_single?: number;
}

interface FormData {
  supplierId: number;
  orderDate: string;
  status: string;
  notes: string;
  items: SupplierOrderItem[];
}

// ---- Component ----

const SupplierOrderForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, hasPermission, hasRole } = useAuth();
  const isEditMode = Boolean(id);

  // Form state
  const [formData, setFormData] = useState<FormData>({
    supplierId: 0,
    orderDate: new Date().toISOString().split('T')[0],
    status: 'draft',
    notes: '',
    items: [],
  });

  // Data
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Product dialog
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<number | string>('all');
  const [tempQuantities, setTempQuantities] = useState<{ [key: number]: { box: number; single: number } }>({});
  const [tempPriceLabels, setTempPriceLabels] = useState<{ [key: number]: { each_price: string; offer_price: string } }>({});

  // New product dialog
  const [newProductDialogOpen, setNewProductDialogOpen] = useState(false);
  const [newProductData, setNewProductData] = useState({
    sku: '', name: '', description: '', category_id: '', unit_size: '', qty_in_box: '', unit_type: 'Piece'
  });
  const [creatingProduct, setCreatingProduct] = useState(false);

  // Warning dialog (supplier not selected)
  const [warningDialogOpen, setWarningDialogOpen] = useState(false);

  // Auto-save state
  const [isDraftMode, setIsDraftMode] = useState(false);
  const [draftOrderId, setDraftOrderId] = useState<number | null>(null);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [pendingAutosave, setPendingAutosave] = useState(false);

  // Image preview
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState<{url: string, name: string} | null>(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // ---- Data fetching ----

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (isEditMode && id) {
      fetchOrder();
    }
  }, [id, isEditMode]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [suppliersRes, productsRes, categoriesRes] = await Promise.all([
        api.get('/suppliers?is_active=1'),
        api.get('/supplier-orders/products'),
        api.get('/categories'),
      ]);

      if (suppliersRes.data.success) setSuppliers(suppliersRes.data.data);
      if (productsRes.data.success) setProducts(productsRes.data.data);
      if (categoriesRes.data.success) {
        setCategories([{ id: 'all', name: 'All Categories' }, ...categoriesRes.data.data]);
      }
    } catch (error) {
      console.error('Error fetching initial data:', error);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrder = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/supplier-orders/${id}`);
      if (response.data.success) {
        const order = response.data.data;

        if (order.status === 'draft') {
          setIsDraftMode(true);
          setDraftOrderId(parseInt(id!));
        } else {
          setDraftOrderId(parseInt(id!));
        }

        setFormData({
          supplierId: order.supplier_id,
          orderDate: order.order_date ? order.order_date.split('T')[0] : '',
          status: order.status,
          notes: order.notes || '',
          items: (order.items || []).map((item: any) => ({
            id: item.id,
            product_id: item.product_id,
            product_name: item.product_name,
            product_sku: item.product_sku,
            unit_size: item.unit_size || '',
            unit_type: item.unit_type || '',
            qty_in_box: item.qty_in_box || 0,
            cost_price: parseFloat(item.cost_price) || 0,
            box_quantity: item.box_quantity || 0,
            single_quantity: item.single_quantity || 0,
            each_price: item.each_price || '',
            offer_price: item.offer_price || '',
            notes: item.notes || '',
            is_new_product: false,
            image_url: item.image_url,
            stock_quantity: item.stock_quantity,
            stock_single: item.stock_single,
          })),
        });
      }
    } catch (error) {
      console.error('Error fetching order:', error);
      setError('Failed to load order');
    } finally {
      setLoading(false);
    }
  };

  // ---- Auto-save ----

  const debounce = (func: Function, delay: number) => {
    let timeoutId: NodeJS.Timeout;
    let lastArgs: any[] | null = null;

    return (...args: any[]) => {
      lastArgs = args;
      setPendingAutosave(true);
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        if (lastArgs && !isAutoSaving) {
          func.apply(null, lastArgs);
          lastArgs = null;
          setPendingAutosave(false);
        }
      }, delay);
    };
  };

  const autosaveOrder = useCallback(async (data: FormData) => {
    if (!data.items.length || data.supplierId === 0) return;
    if (isAutoSaving) return;

    try {
      setIsAutoSaving(true);
      setPendingAutosave(false);

      const orderData = {
        supplierId: data.supplierId,
        orderDate: data.orderDate,
        status: (isEditMode || isDraftMode) ? data.status : 'draft',
        notes: data.notes,
        items: data.items,
        isAutosave: true,
      };

      let response;
      if (isEditMode && id) {
        response = await api.put(`/supplier-orders/${id}`, orderData);
      } else if (isDraftMode && draftOrderId) {
        response = await api.put(`/supplier-orders/${draftOrderId}`, orderData);
      } else {
        response = await api.post('/supplier-orders', orderData);
        if (response.data.success) {
          setDraftOrderId(response.data.data.id);
          setIsDraftMode(true);
        }
      }

      if (response.data.success) {
        setLastSaved(new Date());
      }
    } catch (error: any) {
      console.error('Autosave failed:', error);
      if (error.response?.status === 400) {
        setSnackbar({ open: true, message: error.response.data.message || 'Save failed', severity: 'error' });
      }
    } finally {
      setIsAutoSaving(false);
    }
  }, [isDraftMode, draftOrderId, isEditMode, id, isAutoSaving]);

  const debouncedAutosave = useMemo(
    () => debounce(autosaveOrder, 1000),
    [autosaveOrder]
  );

  const triggerAutosave = (newFormData: FormData) => {
    if (newFormData.items.length > 0 && newFormData.supplierId > 0) {
      debouncedAutosave(newFormData);
    }
  };

  // ---- Product dialog ----

  const handleOpenProductDialog = () => {
    if (formData.supplierId === 0) {
      setWarningDialogOpen(true);
      return;
    }

    // Init temp quantities from existing items
    const initial: { [key: number]: { box: number; single: number } } = {};
    const initialPrices: { [key: number]: { each_price: string; offer_price: string } } = {};
    formData.items.forEach(item => {
      if (item.product_id) {
        initial[item.product_id] = { box: item.box_quantity, single: item.single_quantity };
        initialPrices[item.product_id] = { each_price: item.each_price || '', offer_price: item.offer_price || '' };
      }
    });
    setTempQuantities(initial);
    setTempPriceLabels(initialPrices);
    setSearchTerm('');
    setSelectedCategory('all');
    setProductDialogOpen(true);
  };

  const handleCloseProductDialog = () => {
    setTempQuantities({});
    setTempPriceLabels({});
    setProductDialogOpen(false);
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = !searchTerm ||
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.sku.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory = selectedCategory === 'all' || product.category_id === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  const handleCategoryChange = (_event: React.SyntheticEvent, newValue: number | string) => {
    setSelectedCategory(newValue);
  };

  const handleQuantityUpdate = (product: Product, boxQty: number, singleQty: number) => {
    const existingIndex = formData.items.findIndex(item => item.product_id === product.id);
    const priceLabels = tempPriceLabels[product.id] ?? { each_price: '', offer_price: '' };

    let newItems: SupplierOrderItem[];

    if (boxQty === 0 && singleQty === 0) {
      // Remove item — keep tempPriceLabels so labels aren't lost if user accidentally hits zero
      newItems = formData.items.filter(item => item.product_id !== product.id);
    } else if (existingIndex >= 0) {
      // Update existing — also sync price labels
      newItems = [...formData.items];
      newItems[existingIndex] = {
        ...newItems[existingIndex],
        box_quantity: boxQty,
        single_quantity: singleQty,
        each_price: priceLabels.each_price,
        offer_price: priceLabels.offer_price,
      };
    } else {
      // Add new item with snapshot, using any price labels already typed
      newItems = [...formData.items, {
        product_id: product.id,
        product_name: product.name,
        product_sku: product.sku,
        unit_size: product.unit_size || '',
        unit_type: product.unit_type || '',
        qty_in_box: product.qty_in_box || 0,
        cost_price: product.cost_price || 0,
        box_quantity: boxQty,
        single_quantity: singleQty,
        each_price: priceLabels.each_price,
        offer_price: priceLabels.offer_price,
        notes: '',
        is_new_product: false,
        image_url: product.image_url,
        stock_quantity: product.stock_quantity,
        stock_single: product.stock_single,
      }];
    }

    const newFormData = { ...formData, items: newItems };
    setFormData(newFormData);
    triggerAutosave(newFormData);
  };

  // ---- New product dialog ----

  const handleCreateNewProduct = async () => {
    if (!newProductData.sku || !newProductData.name) {
      setSnackbar({ open: true, message: 'SKU and name are required', severity: 'error' });
      return;
    }

    try {
      setCreatingProduct(true);
      const response = await api.post('/supplier-orders/products', newProductData);

      if (response.data.success) {
        const newProduct = response.data.data;
        // Refresh products list
        const productsRes = await api.get('/supplier-orders/products');
        if (productsRes.data.success) setProducts(productsRes.data.data);

        // Add to order with 1 box qty
        const newItem: SupplierOrderItem = {
          product_id: newProduct.id,
          product_name: newProduct.name,
          product_sku: newProduct.sku,
          unit_size: newProduct.unit_size || '',
          unit_type: newProduct.unit_type || 'Piece',
          qty_in_box: newProduct.qty_in_box || 0,
          cost_price: 0,
          box_quantity: 1,
          single_quantity: 0,
          each_price: '',
          offer_price: '',
          notes: '',
          is_new_product: false,
          image_url: newProduct.image_url,
          stock_quantity: 0,
          stock_single: 0,
        };

        const newFormData = { ...formData, items: [...formData.items, newItem] };
        setFormData(newFormData);

        // Set temp quantity for dialog
        setTempQuantities(prev => ({ ...prev, [newProduct.id]: { box: 1, single: 0 } }));

        setNewProductDialogOpen(false);
        setNewProductData({ sku: '', name: '', description: '', category_id: '', unit_size: '', qty_in_box: '', unit_type: 'Piece' });
        setSnackbar({ open: true, message: 'Product created and added to order', severity: 'success' });

        triggerAutosave(newFormData);
      }
    } catch (error: any) {
      console.error('Error creating product:', error);
      setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to create product', severity: 'error' });
    } finally {
      setCreatingProduct(false);
    }
  };

  // ---- Item management (main form) ----

  const handleRemoveItem = (index: number) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    const newFormData = { ...formData, items: newItems };
    setFormData(newFormData);
    triggerAutosave(newFormData);
  };

  const handleItemBoxQtyChange = (index: number, value: number) => {
    const qty = Math.max(0, value);
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], box_quantity: qty };
    const newFormData = { ...formData, items: newItems };
    setFormData(newFormData);
    triggerAutosave(newFormData);
  };

  const handleItemSingleQtyChange = (index: number, value: number) => {
    const qty = Math.max(0, value);
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], single_quantity: qty };
    const newFormData = { ...formData, items: newItems };
    setFormData(newFormData);
    triggerAutosave(newFormData);
  };

  const handleItemEachPriceChange = (index: number, value: string) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], each_price: value };
    const newFormData = { ...formData, items: newItems };
    setFormData(newFormData);
    triggerAutosave(newFormData);
  };

  const handleItemOfferPriceChange = (index: number, value: string) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], offer_price: value };
    const newFormData = { ...formData, items: newItems };
    setFormData(newFormData);
    triggerAutosave(newFormData);
  };

  // ---- Image preview ----

  const handleImagePreview = (imageUrl: string, productName: string) => {
    setPreviewImage({ url: imageUrl, name: productName });
    setImagePreviewOpen(true);
  };

  const handleCloseImagePreview = () => {
    setImagePreviewOpen(false);
    setPreviewImage(null);
  };

  // ---- Form field changes ----

  const handleSupplierChange = (e: any) => {
    const newFormData = { ...formData, supplierId: e.target.value };
    setFormData(newFormData);
    triggerAutosave(newFormData);
  };

  const handleFieldChange = (field: string, value: any) => {
    const newFormData = { ...formData, [field]: value };
    setFormData(newFormData);
    triggerAutosave(newFormData);
  };

  // ---- Explicit save ----

  const handleSave = async () => {
    if (formData.supplierId === 0) {
      setSnackbar({ open: true, message: 'Please select a supplier', severity: 'error' });
      return;
    }
    if (formData.items.length === 0) {
      setSnackbar({ open: true, message: 'Please add at least one item', severity: 'error' });
      return;
    }

    try {
      setSaving(true);
      const orderData = {
        supplierId: formData.supplierId,
        orderDate: formData.orderDate,
        status: formData.status === 'draft' ? 'pending' : formData.status,
        notes: formData.notes,
        items: formData.items,
        isAutosave: false,
      };

      if (isEditMode && id) {
        await api.put(`/supplier-orders/${id}`, orderData);
      } else if (isDraftMode && draftOrderId) {
        await api.put(`/supplier-orders/${draftOrderId}`, orderData);
      } else {
        await api.post('/supplier-orders', orderData);
      }

      navigate('/supplier-orders');
    } catch (error: any) {
      console.error('Error saving order:', error);
      setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to save order', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // ---- Render ----

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/supplier-orders')}>
              Back
            </Button>
            <Typography variant="h4">
              {isEditMode ? 'Edit Supplier Order' : 'New Supplier Order'}
            </Typography>
            {isDraftMode && <Chip label="Draft" color="warning" size="small" />}
          </Box>
          <Box display="flex" alignItems="center" gap={2}>
            {isAutoSaving && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">Saving...</Typography>
              </Box>
            )}
            {lastSaved && !isAutoSaving && (
              <Typography variant="caption" color="text.secondary">
                Last saved: {lastSaved.toLocaleTimeString()}
              </Typography>
            )}
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Form fields */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, mb: 3 }}>
          <FormControl sx={{ minWidth: 250 }} required>
            <InputLabel>Supplier</InputLabel>
            <Select value={formData.supplierId || ''} label="Supplier" onChange={handleSupplierChange}>
              {suppliers.map(s => (
                <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Order Date"
            type="date"
            value={formData.orderDate}
            onChange={(e) => handleFieldChange('orderDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 200 }}
          />

          {isEditMode && (
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select value={formData.status} label="Status" onChange={(e) => handleFieldChange('status', e.target.value)}>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="sent">Sent</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          )}
        </Box>

        <TextField
          label="Notes"
          value={formData.notes}
          onChange={(e) => handleFieldChange('notes', e.target.value)}
          fullWidth
          multiline
          rows={2}
          sx={{ mb: 3 }}
        />

        <Divider sx={{ mb: 3 }} />

        {/* Items section */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            Order Items ({formData.items.length})
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenProductDialog}>
            Add Items
          </Button>
        </Box>

        {formData.items.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography color="text.secondary">No items added yet. Click "Add Items" to select products.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={50}>Image</TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell align="center">Box Qty</TableCell>
                  <TableCell align="center">Single Qty</TableCell>
                  <TableCell>Each Price</TableCell>
                  <TableCell>Offer Price</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {formData.items.map((item, index) => (
                  <TableRow key={item.product_id || index}>
                    <TableCell>
                      <Box
                        component="img"
                        src={getImageUrl(item.image_url || null)}
                        alt={item.product_name}
                        onClick={() => handleImagePreview(getImageUrl(item.image_url || null), item.product_name)}
                        sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1, cursor: 'pointer', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.1)' } }}
                      />
                    </TableCell>
                    <TableCell>{item.product_sku}</TableCell>
                    <TableCell>{item.product_name}</TableCell>
                    <TableCell>{item.unit_size && item.qty_in_box ? `${item.unit_size} x ${item.qty_in_box}` : (item.unit_size || '-')}</TableCell>
                    <TableCell align="center">
                      <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                        <IconButton size="small" onClick={() => handleItemBoxQtyChange(index, item.box_quantity - 1)}>
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <TextField
                          value={item.box_quantity}
                          onChange={(e) => handleItemBoxQtyChange(index, parseInt(e.target.value) || 0)}
                          onFocus={(e) => e.target.select()}
                          inputProps={{ style: { textAlign: 'center', width: 40 } }}
                          size="small"
                          variant="outlined"
                        />
                        <IconButton size="small" onClick={() => handleItemBoxQtyChange(index, item.box_quantity + 1)}>
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                        <IconButton size="small" onClick={() => handleItemSingleQtyChange(index, item.single_quantity - 1)}>
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <TextField
                          value={item.single_quantity}
                          onChange={(e) => handleItemSingleQtyChange(index, parseInt(e.target.value) || 0)}
                          onFocus={(e) => e.target.select()}
                          inputProps={{ style: { textAlign: 'center', width: 40 } }}
                          size="small"
                          variant="outlined"
                        />
                        <IconButton size="small" onClick={() => handleItemSingleQtyChange(index, item.single_quantity + 1)}>
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={item.each_price}
                        onChange={(e) => handleItemEachPriceChange(index, e.target.value)}
                        multiline
                        inputProps={{ maxLength: 100 }}
                        sx={{ width: 130 }}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={item.offer_price}
                        onChange={(e) => handleItemOfferPriceChange(index, e.target.value)}
                        multiline
                        inputProps={{ maxLength: 100 }}
                        sx={{ width: 130 }}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" color="error" onClick={() => handleRemoveItem(index)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Action buttons */}
        <Box display="flex" justifyContent="flex-end" gap={2}>
          <Button variant="outlined" onClick={() => navigate('/supplier-orders')}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving || formData.supplierId === 0 || formData.items.length === 0}
          >
            {saving ? 'Saving...' : (isEditMode ? 'Update Order' : 'Save Order')}
          </Button>
        </Box>
      </Paper>

      {/* ---- Product Selection Dialog ---- */}
      <Dialog open={productDialogOpen} onClose={handleCloseProductDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6">Select Products</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {filteredProducts.length} products
                {(searchTerm || selectedCategory !== 'all') && ` (filtered from ${products.length} total)`}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setNewProductDialogOpen(true)}
            >
              New Product
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {/* Category Tabs */}
          <Paper sx={{ mb: 2, flexShrink: 0 }}>
            <Tabs
              value={selectedCategory}
              onChange={handleCategoryChange}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
            >
              {categories.map((category) => (
                <Tab key={category.id} label={category.name} value={category.id} />
              ))}
            </Tabs>
          </Paper>

          {/* Search */}
          <TextField
            placeholder="Search by product name or SKU..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />

          {/* Products table */}
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell width={50}>Image</TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell>Product Name</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell align="center">Stock (B)</TableCell>
                  <TableCell align="center">Stock (S)</TableCell>
                  <TableCell align="center">Box Qty</TableCell>
                  <TableCell align="center">Single Qty</TableCell>
                  <TableCell>Each Price</TableCell>
                  <TableCell>Offer Price</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredProducts.map((product) => {
                  const existingItem = formData.items.find(item => item.product_id === product.id);
                  const tempBoxQty = tempQuantities[product.id]?.box ?? existingItem?.box_quantity ?? 0;
                  const tempSingleQty = tempQuantities[product.id]?.single ?? existingItem?.single_quantity ?? 0;
                  const isSelected = existingItem !== undefined || tempBoxQty > 0 || tempSingleQty > 0;

                  const tempEachPrice = tempPriceLabels[product.id]?.each_price ?? existingItem?.each_price ?? '';
                  const tempOfferPrice = tempPriceLabels[product.id]?.offer_price ?? existingItem?.offer_price ?? '';

                  const setBoxQty = (value: number) => {
                    const qty = Math.max(0, value);
                    const singleQty = tempQuantities[product.id]?.single ?? existingItem?.single_quantity ?? 0;
                    setTempQuantities(prev => ({ ...prev, [product.id]: { box: qty, single: singleQty } }));
                    handleQuantityUpdate(product, qty, singleQty);
                  };

                  const setSingleQty = (value: number) => {
                    const qty = Math.max(0, value);
                    const boxQty = tempQuantities[product.id]?.box ?? existingItem?.box_quantity ?? 0;
                    setTempQuantities(prev => ({ ...prev, [product.id]: { box: boxQty, single: qty } }));
                    handleQuantityUpdate(product, boxQty, qty);
                  };

                  const setEachPrice = (value: string) => {
                    setTempPriceLabels(prev => ({
                      ...prev,
                      [product.id]: { each_price: value, offer_price: prev[product.id]?.offer_price ?? existingItem?.offer_price ?? '' },
                    }));
                    // Sync to order item if product is already added
                    setFormData(prev => {
                      const idx = prev.items.findIndex(item => item.product_id === product.id);
                      if (idx >= 0) {
                        const newItems = [...prev.items];
                        newItems[idx] = { ...newItems[idx], each_price: value };
                        const newFormData = { ...prev, items: newItems };
                        triggerAutosave(newFormData);
                        return newFormData;
                      }
                      return prev;
                    });
                  };

                  const setOfferPrice = (value: string) => {
                    setTempPriceLabels(prev => ({
                      ...prev,
                      [product.id]: { each_price: prev[product.id]?.each_price ?? existingItem?.each_price ?? '', offer_price: value },
                    }));
                    // Sync to order item if product is already added
                    setFormData(prev => {
                      const idx = prev.items.findIndex(item => item.product_id === product.id);
                      if (idx >= 0) {
                        const newItems = [...prev.items];
                        newItems[idx] = { ...newItems[idx], offer_price: value };
                        const newFormData = { ...prev, items: newItems };
                        triggerAutosave(newFormData);
                        return newFormData;
                      }
                      return prev;
                    });
                  };

                  return (
                    <TableRow
                      key={product.id}
                      sx={{
                        backgroundColor: isSelected ? 'action.selected' : 'inherit',
                        '&:hover': { backgroundColor: 'action.hover' },
                      }}
                    >
                      <TableCell>
                        <Box
                          component="img"
                          src={getImageUrl(product.image_url)}
                          alt={product.name}
                          onClick={() => handleImagePreview(getImageUrl(product.image_url), product.name)}
                          sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1, cursor: 'pointer', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.1)' } }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={isSelected ? 'bold' : 'normal'}>
                          {product.sku}
                        </Typography>
                      </TableCell>
                      <TableCell>{product.name}</TableCell>
                      <TableCell>{product.unit_size && product.qty_in_box ? `${product.unit_size} x ${product.qty_in_box}` : (product.unit_size || '-')}</TableCell>
                      <TableCell align="center">
                        <Chip label={product.stock_quantity ?? 0} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={product.stock_single ?? 0} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                          <IconButton size="small" onClick={() => setBoxQty(tempBoxQty - 1)}>
                            <RemoveIcon fontSize="small" />
                          </IconButton>
                          <TextField
                            value={tempBoxQty}
                            onChange={(e) => setBoxQty(parseInt(e.target.value) || 0)}
                            onFocus={(e) => e.target.select()}
                            inputProps={{ style: { textAlign: 'center', width: 35 } }}
                            size="small"
                            variant="outlined"
                          />
                          <IconButton size="small" onClick={() => setBoxQty(tempBoxQty + 1)}>
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                          <IconButton size="small" onClick={() => setSingleQty(tempSingleQty - 1)}>
                            <RemoveIcon fontSize="small" />
                          </IconButton>
                          <TextField
                            value={tempSingleQty}
                            onChange={(e) => setSingleQty(parseInt(e.target.value) || 0)}
                            onFocus={(e) => e.target.select()}
                            inputProps={{ style: { textAlign: 'center', width: 35 } }}
                            size="small"
                            variant="outlined"
                          />
                          <IconButton size="small" onClick={() => setSingleQty(tempSingleQty + 1)}>
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <TextField
                          value={tempEachPrice}
                          onChange={(e) => setEachPrice(e.target.value)}
                          placeholder="e.g. £2.50 each"
                          inputProps={{ maxLength: 100, style: { width: 95 } }}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          value={tempOfferPrice}
                          onChange={(e) => setOfferPrice(e.target.value)}
                          placeholder="e.g. Buy 2 get 1"
                          inputProps={{ maxLength: 100, style: { width: 95 } }}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseProductDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ---- New Product Dialog ---- */}
      <Dialog open={newProductDialogOpen} onClose={() => setNewProductDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Product</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
            <TextField
              label="SKU"
              value={newProductData.sku}
              onChange={(e) => setNewProductData(prev => ({ ...prev, sku: e.target.value }))}
              required
              fullWidth
            />
            <TextField
              label="Product Name"
              value={newProductData.name}
              onChange={(e) => setNewProductData(prev => ({ ...prev, name: e.target.value }))}
              required
              fullWidth
            />
            <TextField
              label="Description"
              value={newProductData.description}
              onChange={(e) => setNewProductData(prev => ({ ...prev, description: e.target.value }))}
              fullWidth
              sx={{ gridColumn: '1 / span 2' }}
            />
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={newProductData.category_id}
                label="Category"
                onChange={(e) => setNewProductData(prev => ({ ...prev, category_id: e.target.value as string }))}
              >
                {categories.filter(c => c.id !== 'all').map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Unit Size"
              value={newProductData.unit_size}
              onChange={(e) => setNewProductData(prev => ({ ...prev, unit_size: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Qty in Box"
              type="number"
              value={newProductData.qty_in_box}
              onChange={(e) => setNewProductData(prev => ({ ...prev, qty_in_box: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Unit Type"
              value={newProductData.unit_type}
              onChange={(e) => setNewProductData(prev => ({ ...prev, unit_type: e.target.value }))}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewProductDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateNewProduct} disabled={creatingProduct}>
            {creatingProduct ? 'Creating...' : 'Create Product'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Warning Dialog (no supplier selected) ---- */}
      <Dialog open={warningDialogOpen} onClose={() => setWarningDialogOpen(false)}>
        <DialogTitle>Select Supplier First</DialogTitle>
        <DialogContent>
          <Typography>Please select a supplier before adding items to the order.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWarningDialogOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>

      {/* ---- Snackbar ---- */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Image Preview Dialog */}
      <Dialog
        open={imagePreviewOpen}
        onClose={handleCloseImagePreview}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            bgcolor: 'rgba(0, 0, 0, 0.9)',
            backdropFilter: 'blur(10px)',
          }
        }}
      >
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', p: 2 }}>
            <Typography variant="h6" sx={{ color: 'white', flexGrow: 1 }}>
              {previewImage?.name}
            </Typography>
            <IconButton onClick={handleCloseImagePreview} sx={{ color: 'white' }} size="large">
              <ClearIcon />
            </IconButton>
          </Box>
          {previewImage && (
            <Box
              component="img"
              src={previewImage.url}
              alt={previewImage.name}
              onError={(e) => { (e.target as HTMLImageElement).src = getImageUrl(null); }}
              sx={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: 1, cursor: 'pointer' }}
              onClick={handleCloseImagePreview}
            />
          )}
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', textAlign: 'center', p: 2, fontSize: '0.875rem' }}>
            Tap image or close button to exit
          </Typography>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default SupplierOrderForm;
